import type { FileItem, PermitItem, WarrantyItem } from "@nmt/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { errorMessage, get, patch, post } from "../api";
import { fromLocalInput, toLocalInput } from "../components/schedule";
import { Alert, Badge, Button, Dialog, Empty, Field, Loading, Panel, PanelHeader, SelectField, TextAreaField, formatCents, formatDate, formatDateTime, parseDollars } from "../components/ui";
import { useSession } from "../session";
import { RichText } from "../components/rich-text";

const PERMIT_STATUS: Record<PermitItem["status"], { label: string; tone: "ok" | "wait" | "off" | "brand" }> = {
  not_applied: { label: "Not applied", tone: "off" },
  applied: { label: "Applied", tone: "wait" },
  approved: { label: "Approved", tone: "ok" },
  expired: { label: "Expired", tone: "brand" },
};

/** Shown on the job when its city needs a permit and there isn't one yet. */
export function PermitReminder({ jobId, city }: { jobId: string; city: string }) {
  const { me } = useSession();
  const needs = !!city && (me?.lists.permitCities ?? []).some((c) => c.toLowerCase() === city.toLowerCase());
  const q = useQuery({ queryKey: ["permits", jobId], queryFn: () => get<PermitItem[]>(`/api/jobs/${jobId}/permits`), enabled: needs });
  if (!needs || !q.data || q.data.length) return null;
  return <Alert tone="info">{city} requires a permit for this kind of work. Add it on the Permits & warranty tab.</Alert>;
}

/** The job's Permits & warranty tab. */
export function JobPermitsWarranty({ jobId }: { jobId: string }) {
  return (
    <div className="flex flex-col gap-5">
      <Permits jobId={jobId} />
      <Warranty jobId={jobId} />
    </div>
  );
}

function Permits({ jobId }: { jobId: string }) {
  const { can } = useSession();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["permits", jobId], queryFn: () => get<PermitItem[]>(`/api/jobs/${jobId}/permits`) });
  const [editing, setEditing] = useState<PermitItem | "new" | null>(null);
  const [inspecting, setInspecting] = useState<{ permit: PermitItem; inspection?: PermitItem["inspections"][number] } | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ["permits", jobId] });
  const edit = can("permits.edit");
  return (
    <Panel>
      <PanelHeader
        title="Permits"
        action={
          edit && (
            <Button className="px-3 py-1.5" onClick={() => setEditing("new")}>
              Add permit
            </Button>
          )
        }
      />
      {q.isLoading ? (
        <div className="px-5"><Loading /></div>
      ) : !q.data?.length ? (
        <Empty>No permits.</Empty>
      ) : (
        <ul>
          {q.data.map((p) => (
            <li key={p.id} className="border-b border-mist px-5 py-4 last:border-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold">{p.number || "Permit"}</span>
                <Badge tone={PERMIT_STATUS[p.status].tone}>{PERMIT_STATUS[p.status].label}</Badge>
                <span className="text-sm text-muted">{[p.city, p.type, p.costCents !== null ? formatCents(p.costCents) : null, p.approvedOn && `approved ${formatDate(p.approvedOn)}`, p.expiresOn && `expires ${formatDate(p.expiresOn)}`].filter(Boolean).join(" · ")}</span>
                {edit && (
                  <span className="ml-auto flex gap-2">
                    <Button variant="quiet" className="px-2 py-1" onClick={() => setInspecting({ permit: p })}>
                      + Inspection
                    </Button>
                    <Button variant="quiet" className="px-2 py-1" onClick={() => setEditing(p)}>
                      Edit
                    </Button>
                  </span>
                )}
              </div>
              {p.file && (
                <a href={p.file.url} target="_blank" rel="noreferrer" className="text-sm text-brand hover:underline">
                  {p.file.name}
                </a>
              )}
              {p.notes && <RichText className="text-sm text-ink-soft" text={p.notes} />}
              {p.inspections.length > 0 && (
                <ul className="mt-2 flex flex-col gap-1 text-sm">
                  {p.inspections.map((i) => (
                    <li key={i.id} className="flex flex-wrap items-center gap-2">
                      <Badge tone={i.result === "pass" ? "ok" : i.result === "fail" ? "brand" : "wait"}>{i.result === "pass" ? "Passed" : i.result === "fail" ? "Failed" : "Scheduled"}</Badge>
                      <span>{i.kind || "Inspection"}</span>
                      <span className="text-muted">{[i.scheduledAt ? formatDateTime(i.scheduledAt) : "no time set", i.inspector].filter(Boolean).join(" · ")}</span>
                      {edit && (
                        <button className="text-muted hover:text-ink" onClick={() => setInspecting({ permit: p, inspection: i })}>
                          Update
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      )}
      {editing && <PermitDialog jobId={jobId} permit={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={() => (setEditing(null), void refresh())} />}
      {inspecting && <InspectionDialog {...inspecting} onClose={() => setInspecting(null)} onSaved={() => (setInspecting(null), void refresh(), void qc.invalidateQueries({ queryKey: ["appointments"] }))} />}
    </Panel>
  );
}

function PermitDialog({ jobId, permit, onClose, onSaved }: { jobId: string; permit: PermitItem | null; onClose: () => void; onSaved: () => void }) {
  const files = useQuery({ queryKey: ["files", "job", jobId], queryFn: () => get<FileItem[]>(`/api/jobs/${jobId}/files`) });
  const [f, setF] = useState({
    number: permit?.number ?? "",
    city: permit?.city ?? "",
    type: permit?.type ?? "",
    cost: permit?.costCents != null ? (permit.costCents / 100).toFixed(2) : "",
    appliedOn: permit?.appliedOn ?? "",
    approvedOn: permit?.approvedOn ?? "",
    expiresOn: permit?.expiresOn ?? "",
    fileId: permit?.file?.id ?? "",
    notes: permit?.notes ?? "",
  });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const cost = parseDollars(f.cost);
  const save = useMutation({
    mutationFn: () => {
      const body = { number: f.number, ...(f.city ? { city: f.city } : {}), type: f.type, costCents: cost === null || Number.isNaN(cost) ? null : cost, appliedOn: f.appliedOn || null, approvedOn: f.approvedOn || null, expiresOn: f.expiresOn || null, fileId: f.fileId || null, notes: f.notes };
      return permit ? patch(`/api/permits/${permit.id}`, body) : post(`/api/jobs/${jobId}/permits`, body);
    },
    onSuccess: onSaved,
  });
  return (
    <Dialog
      title={permit ? "Edit permit" : "Add permit"}
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>Cancel</Button>
          <Button variant="primary" busy={save.isPending} disabled={cost !== null && Number.isNaN(cost)} onClick={() => save.mutate()}>Save</Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {save.error && <Alert>{errorMessage(save.error)}</Alert>}
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Permit #" value={f.number} onChange={set("number")} />
          <Field label="City" value={f.city} onChange={set("city")} placeholder="The job's city" />
          <Field label="Type" value={f.type} onChange={set("type")} placeholder="Re-roof" />
          <Field label="Cost" inputMode="decimal" value={f.cost} onChange={set("cost")} />
          <Field label="Applied" type="date" value={f.appliedOn} onChange={set("appliedOn")} />
          <Field label="Approved" type="date" value={f.approvedOn} onChange={set("approvedOn")} />
          <Field label="Expires" type="date" value={f.expiresOn} onChange={set("expiresOn")} />
          <SelectField label="Document" value={f.fileId} onChange={set("fileId")} hint="Upload it to the job's files first.">
            <option value="">None</option>
            {files.data?.filter((x) => !x.isPhoto).map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
          </SelectField>
        </div>
        <TextAreaField label="Notes" value={f.notes} onChange={set("notes")} rows={2} />
      </div>
    </Dialog>
  );
}

function InspectionDialog({ permit, inspection, onClose, onSaved }: { permit: PermitItem; inspection?: PermitItem["inspections"][number]; onClose: () => void; onSaved: () => void }) {
  const [kind, setKind] = useState(inspection?.kind ?? "");
  const [when, setWhen] = useState(toLocalInput(inspection?.scheduledAt ?? null));
  const [inspector, setInspector] = useState(inspection?.inspector ?? "");
  const [result, setResult] = useState(inspection?.result ?? "pending");
  const [notes, setNotes] = useState(inspection?.notes ?? "");
  const save = useMutation({
    mutationFn: () => {
      const body = { kind, scheduledAt: fromLocalInput(when), inspector, notes };
      return inspection ? patch(`/api/inspections/${inspection.id}`, { ...body, result }) : post(`/api/permits/${permit.id}/inspections`, body);
    },
    onSuccess: onSaved,
  });
  return (
    <Dialog
      title={inspection ? "Update inspection" : "Schedule inspection"}
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>Cancel</Button>
          <Button variant="primary" busy={save.isPending} onClick={() => save.mutate()}>Save</Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {save.error && <Alert>{errorMessage(save.error)}</Alert>}
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Inspection" value={kind} onChange={(e) => setKind(e.target.value)} placeholder="Final" />
          <Field label="When" type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} hint="Goes on your calendar." />
          <Field label="Inspector" value={inspector} onChange={(e) => setInspector(e.target.value)} />
          {inspection && (
            <SelectField label="Result" value={result} onChange={(e) => setResult(e.target.value as typeof result)} hint={result === "fail" ? "A task is made to fix and reschedule." : undefined}>
              <option value="pending">Not yet</option>
              <option value="pass">Passed</option>
              <option value="fail">Failed</option>
            </SelectField>
          )}
        </div>
        <TextAreaField label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
      </div>
    </Dialog>
  );
}

function Warranty({ jobId }: { jobId: string }) {
  const { can } = useSession();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["warranty", jobId], queryFn: () => get<WarrantyItem | null>(`/api/jobs/${jobId}/warranty`) });
  const set = (w: WarrantyItem | null) => qc.setQueryData(["warranty", jobId], w);
  const start = useMutation({ mutationFn: () => post<WarrantyItem>(`/api/jobs/${jobId}/warranty`), onSuccess: set });
  const cert = useMutation({ mutationFn: (id: string) => post<WarrantyItem>(`/api/warranties/${id}/certificate`), onSuccess: set });
  const update = useMutation({ mutationFn: ({ id, ...b }: { id: string; termYears?: number; manufacturerRegisteredOn?: string | null }) => patch<WarrantyItem>(`/api/warranties/${id}`, b), onSuccess: set });
  const [claim, setClaim] = useState("");
  const addClaim = useMutation({ mutationFn: (id: string) => post<WarrantyItem>(`/api/warranties/${id}/claims`, { reportedOn: new Date().toLocaleDateString("en-CA"), description: claim }), onSuccess: (w) => (set(w), setClaim("")) });
  const closeClaim = useMutation({ mutationFn: ({ id, status }: { id: string; status: "resolved" | "denied" | "open" }) => patch<WarrantyItem>(`/api/warranty-claims/${id}`, { status }), onSuccess: set });
  const w = q.data;
  const edit = can("permits.edit");
  const err = start.error ?? cert.error ?? update.error ?? addClaim.error ?? closeClaim.error;
  return (
    <Panel>
      <PanelHeader title="Warranty" description={w ? undefined : "Starts by itself when the job is closed: 5 years on residential roofing, 1 year on everything else."} action={!w && edit && !q.isLoading && <Button className="px-3 py-1.5" busy={start.isPending} onClick={() => start.mutate()}>Start now</Button>} />
      {err && <div className="px-5 pt-3"><Alert>{errorMessage(err)}</Alert></div>}
      {q.isLoading ? (
        <div className="px-5"><Loading /></div>
      ) : !w ? (
        <Empty>No warranty yet.</Empty>
      ) : (
        <div className="flex flex-col gap-3 px-5 py-4 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={w.active ? "ok" : "off"}>{w.active ? "Active" : "Ended"}</Badge>
            <span className="font-semibold">{w.termYears}-year workmanship warranty</span>
            <span className="text-muted">{formatDate(w.startsOn)} – {formatDate(w.endsOn)}</span>
            {edit && (
              <select aria-label="Term" className="ml-auto rounded-[var(--radius-control)] border border-line px-2 py-1" value={w.termYears} onChange={(e) => update.mutate({ id: w.id, termYears: Number(e.target.value) })}>
                {[0, 1, 2, 3, 5, 10].map((y) => <option key={y} value={y}>{y} years</option>)}
              </select>
            )}
          </div>
          {w.manufacturer && (
            <div className="flex flex-wrap items-center gap-2">
              <span>{w.manufacturer} registration:</span>
              {w.manufacturerRegisteredOn ? <span>registered {formatDate(w.manufacturerRegisteredOn)}</span> : <Badge tone="wait">Not registered</Badge>}
              {edit && !w.manufacturerRegisteredOn && <Button variant="quiet" className="px-2 py-1" onClick={() => update.mutate({ id: w.id, manufacturerRegisteredOn: new Date().toLocaleDateString("en-CA") })}>Mark registered today</Button>}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2">
            {w.certificate ? <a href={w.certificate.url} target="_blank" rel="noreferrer" className="font-semibold text-brand hover:underline">{w.certificate.name}</a> : <span className="text-muted">No certificate yet.</span>}
            {edit && <Button variant="quiet" className="px-2 py-1" busy={cert.isPending} onClick={() => cert.mutate(w.id)}>{w.certificate ? "Make again" : "Make certificate"}</Button>}
          </div>
          <div>
            <div className="mb-1 font-semibold text-ink-soft">Claims</div>
            {!w.claims.length ? <p className="text-muted">None.</p> : (
              <ul className="flex flex-col gap-1">
                {w.claims.map((c) => (
                  <li key={c.id} className="flex flex-wrap items-center gap-2">
                    <Badge tone={c.status === "open" ? "wait" : c.status === "resolved" ? "ok" : "off"}>{c.status === "open" ? "Open" : c.status === "resolved" ? "Resolved" : "Denied"}</Badge>
                    <span>{formatDate(c.reportedOn)}: {c.description}</span>
                    {edit && c.status === "open" && <button className="text-muted hover:text-ink" onClick={() => closeClaim.mutate({ id: c.id, status: "resolved" })}>Mark resolved</button>}
                  </li>
                ))}
              </ul>
            )}
            {edit && (
              <div className="mt-2 flex gap-2">
                <input aria-label="New claim" className="flex-1 rounded-[var(--radius-control)] border border-line px-3 py-1.5" placeholder="What's the problem?" value={claim} onChange={(e) => setClaim(e.target.value)} />
                <Button className="px-3 py-1.5" disabled={!claim.trim()} busy={addClaim.isPending} onClick={() => addClaim.mutate(w.id)}>Add claim</Button>
              </div>
            )}
          </div>
        </div>
      )}
    </Panel>
  );
}
